import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { BrandMark } from "@/components/BrandMark";
import {
  ArrowRight, ShieldCheck, Building2, Building, Landmark, Users, FileText, CheckCircle2,
  CalendarDays, ClipboardList, ScrollText, BellRing, MessageSquareText, KeyRound, Wallet,
  Sparkles, Smartphone, Scale, ArrowDownRight, ArrowUpRight,
} from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen bg-sand-50 text-ink font-sans">
      <TopNav />
      <Hero />
      <ScaleSection />
      <RunsOn />
      <TenantsSection />
      <TapOfButton />
      <ComingSoon />
      <Governance />
      <ComparisonTable />
      <HowItWorks />
      <Pricing />
      <ContactForm />
      <Footer />
    </div>
  );
}

function TopNav() {
  return (
    <header className="max-w-7xl mx-auto flex items-center justify-between px-6 lg:px-10 py-5" data-testid="landing-nav">
      <BrandMark size={52} />
      <nav className="hidden md:flex items-center gap-8 text-sm text-stone-700">
        <a href="#runs-on" className="hover:text-ink">What it runs</a>
        <a href="#tap" className="hover:text-ink">On your phone</a>
        <a href="#how" className="hover:text-ink">How it works</a>
        <a href="#pricing" className="hover:text-ink">Pricing</a>
      </nav>
      <div className="flex items-center gap-3">
        <Link to="/admin/login" data-testid="nav-admin-login" className="hidden sm:inline-flex text-sm text-stone-700 hover:text-ink">Portal login</Link>
        <a href="#contact" data-testid="nav-cta" className="inline-flex items-center gap-2 rounded-full bg-ink text-white px-5 py-2.5 text-sm font-medium hover:bg-stone-800 transition-colors">Request a Demo <ArrowRight size={16} /></a>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden" data-testid="landing-hero">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 px-6 lg:px-10 pt-8 lg:pt-16 pb-24 lg:pb-32">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 rounded-full border border-stone-300 bg-white px-3 py-1 text-xs uppercase tracking-widest text-stone-600 mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-terracotta" />
            Made in India · Hosted in India
          </div>
          <h1 className="editorial text-5xl sm:text-6xl lg:text-[76px] leading-[1.02] font-medium tracking-tight text-ink">
            Empowering Housing<br/>and Commercial Societies<br/>
            <span className="italic text-terracotta">to Self-Manage.</span>
          </h1>
          <p className="mt-8 max-w-xl text-lg text-stone-600 leading-relaxed">
            Whether you're a standalone building, a mid-sized complex, or a large township — The Society OS automates
            the everyday running of your society, so residents and committee members spend less time chasing paperwork
            and more time actually living.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <a href="#contact" data-testid="hero-cta-primary" className="inline-flex items-center gap-2 rounded-full bg-terracotta hover:bg-terracotta-hover text-white px-7 py-3.5 text-base font-medium transition-colors">Request a Demo <ArrowRight size={18} /></a>
            <a href="#how" data-testid="hero-cta-secondary" className="inline-flex items-center rounded-full border border-stone-300 bg-white text-ink px-7 py-3.5 text-base font-medium hover:bg-sand-100 transition-colors">See how it works</a>
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-6 text-sm text-stone-500">
            <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-emerald-700"/> No app to install</span>
            <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-emerald-700"/> OTP-only for seniors</span>
            <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-emerald-700"/> India-only hosting</span>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="relative rounded-3xl overflow-hidden border border-stone-200 bg-sand-100 aspect-[4/5]">
            <img src="https://images.unsplash.com/photo-1775954636266-3440f53a9b31?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBpbmRpYW4lMjBhcGFydG1lbnQlMjBidWlsZGluZyUyMGV4dGVyaW9yJTIwc3Vuc2V0fGVufDB8fHx8MTc4NDMwNzkyNHww&ixlib=rb-4.1.0&q=85"
              alt="Modern apartment building at sunset" className="w-full h-full object-cover"/>
            <div className="grain absolute inset-0" />
            <div className="absolute bottom-5 left-5 right-5 bg-white/95 backdrop-blur rounded-2xl border border-stone-200 p-5">
              <div className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-2">Owner Workspace</div>
              <div className="editorial text-2xl leading-tight">Clubhouse — Sun 12 Oct, 4–6 PM</div>
              <div className="text-sm text-stone-500 mt-2">Confirmed · Booking fee ₹500 · Invoice generated</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ScaleSection() {
  const tiers = [
    { icon: Building, k: "Standalone Building", d: "Simple setup, quick onboarding, everything a single-building society needs from day one." },
    { icon: Building2, k: "Mid-Sized Complex", d: "Multiple buildings and wings, unified reporting, one committee dashboard for the whole complex." },
    { icon: Landmark, k: "Large Township", d: "Handles scale — hundreds of units across buildings, blocks, and shops — without losing the simplicity residents expect." },
  ];
  return (
    <section id="scale" className="py-24 lg:py-32" data-testid="landing-scale">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-14">
          <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-4 font-semibold">Built for Every Kind of Society</div>
          <h2 className="editorial text-4xl lg:text-5xl leading-tight tracking-tight">One system,<br/>any size, any structure.</h2>
          <p className="mt-6 text-stone-600 text-lg leading-relaxed max-w-2xl">
            A single building with twelve flats runs differently from a township with thirty. The Society OS scales with you —
            the same system that handles one building's maintenance cycle also manages multi-building townships with different
            wings, blocks, and shops, without asking your residents to learn something new as you grow.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {tiers.map((p, i) => (
            <div key={i} className="bg-white border border-stone-200 rounded-2xl p-7" data-testid={`scale-tier-${i}`}>
              <div className="w-11 h-11 rounded-xl bg-terracotta/10 text-terracotta flex items-center justify-center mb-5"><p.icon size={22} strokeWidth={1.5}/></div>
              <h3 className="editorial text-2xl leading-snug mb-3">{p.k}</h3>
              <p className="text-stone-600 leading-relaxed">{p.d}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 rounded-2xl border border-stone-200 bg-white p-6 lg:p-8 max-w-4xl">
          <p className="text-stone-700 leading-relaxed">
            Run by a full-time professional management agency, or entirely by resident volunteers giving their evenings
            and weekends — <span className="text-ink font-medium">The Society OS is built for both.</span> It doesn't
            assume you have staff. It replaces the need for them to do the repetitive parts.
          </p>
        </div>
      </div>
    </section>
  );
}

function RunsOn() {
  const blocks = [
    {
      icon: FileText,
      title: "Invoicing & Reminders",
      body: "Maintenance, sinking fund, late fees, and any special charges — raised, verified, and sent automatically to every owner's dashboard, email, and printable PDF. No more manually preparing receipts or reminding people door to door.",
      without: "Someone spends every month re-typing the same invoice, chasing ten owners individually, and hoping the register doesn't go missing.",
    },
    {
      icon: MessageSquareText,
      title: "Complaints & Issues",
      body: "A leaking pipe, a parking dispute, a broken lift — residents raise it once, and it goes straight to the right committee member with visible status updates, instead of getting lost in fifty unrelated messages in a group chat.",
      without: "Complaints get repeated in every group chat until someone finally responds, and there's no record of what was fixed and when.",
    },
    {
      icon: ClipboardList,
      title: "Applications, Approvals & NOCs",
      body: "Minor work permission, major renovation approval, unit transfer requests, NOCs, account statements — every application follows a clear, trackable approval path from submission to decision, with dates and remarks retained.",
      without: "An application means printing a letter, hand-delivering it, and following up in person to know if it was even seen.",
    },
    {
      icon: ScrollText,
      title: "Rules, Notices & Broadcasts",
      body: "Every notice, meeting minute, bye-law, and society rule lives in one place residents can actually find later — plus urgent broadcast alerts (fire, safety, emergencies) that reach every resident and tenant instantly.",
      without: "Bye-laws exist in a PDF someone emailed in 2019, and nobody can find the actual rule when a dispute comes up.",
    },
  ];
  return (
    <section id="runs-on" className="py-24 lg:py-32 bg-sand-100 border-y border-stone-200" data-testid="landing-runs-on">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-14">
          <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-4 font-semibold">Everything Your Society Runs On, In One Place</div>
          <h2 className="editorial text-4xl lg:text-5xl leading-tight tracking-tight">Stop running your society out of<br/>notebooks, WhatsApp groups,<br/><span className="italic text-terracotta">and memory.</span></h2>
          <p className="mt-6 text-stone-600 text-lg leading-relaxed">
            Every society ends up managing the same things — dues, complaints, approvals, notices — usually through a mix
            of registers, WhatsApp groups, and someone's personal phone number. The Society OS brings all of it into one
            system built specifically for how societies actually work.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {blocks.map((b, i) => (
            <div key={i} className="rounded-2xl bg-white border border-stone-200 p-7 flex flex-col" data-testid={`runs-on-block-${i}`}>
              <div className="w-11 h-11 rounded-xl bg-terracotta/10 text-terracotta flex items-center justify-center mb-5"><b.icon size={22} strokeWidth={1.5}/></div>
              <h3 className="editorial text-2xl leading-snug mb-3">{b.title}</h3>
              <p className="text-stone-600 leading-relaxed">{b.body}</p>
              <div className="mt-5 pt-5 border-t border-stone-200">
                <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold mb-1.5 flex items-center gap-1.5">
                  <ArrowDownRight size={12} className="text-stone-500"/> Without this
                </div>
                <p className="text-stone-600 leading-relaxed italic text-sm">{b.without}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TenantsSection() {
  return (
    <section className="py-20 lg:py-28" data-testid="landing-tenants">
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-4 font-semibold">Tenants Are Part of the Community Too</div>
        <h2 className="editorial text-4xl lg:text-5xl leading-tight tracking-tight">
          Not just for owners.
        </h2>
        <p className="mt-6 text-stone-600 text-lg leading-relaxed">
          Tenants live in the society too — they deal with the same leaking taps, the same lift breakdowns, the same
          emergencies. The Society OS gives tenants their own access to raise complaints, receive tenant-specific
          notices, and see broadcast alerts, so the people actually living on the ground are never left out of the
          loop, without handing them access to ownership or financial matters that aren't theirs to see.
        </p>
      </div>
    </section>
  );
}

function TapOfButton() {
  const items = [
    "Book a common resource — clubhouse, guest room, sports court — and know instantly if it's confirmed",
    "See your maintenance invoice the moment it's issued, not whenever someone remembers to tell you",
    "Apply for an NOC, transfer, or work permission and track exactly where it stands",
    "Raise a complaint and follow its status yourself — no need to WhatsApp or chase anyone down",
    "Get replies to your queries directly against the notice or invoice you asked about",
  ];
  return (
    <section id="tap" className="py-24 lg:py-32 bg-sand-100 border-y border-stone-200" data-testid="landing-tap">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-4 font-semibold">Everything, At the Tap of a Button</div>
          <h2 className="editorial text-4xl lg:text-5xl leading-tight tracking-tight">Your society,<br/>on your phone.</h2>
          <p className="mt-6 text-stone-600 text-lg leading-relaxed">
            Book the clubhouse for a birthday party. Check if this month's maintenance invoice has arrived. Apply
            for a work permission for that bathroom renovation. Report a water leakage in the basement. Every one
            of these used to mean a phone call, a WhatsApp message to the secretary, or standing in line at
            someone's door. Now it's a few taps, from wherever you are.
          </p>
          <div className="mt-8 inline-flex items-center gap-2 text-stone-500 text-sm">
            <Smartphone size={16}/> Works from any phone browser — no app to install.
          </div>
        </div>
        <div className="lg:col-span-7">
          <ul className="bg-white border border-stone-200 rounded-2xl divide-y divide-stone-200 overflow-hidden">
            {items.map((t, i) => (
              <li key={i} className="flex items-start gap-4 p-5 lg:p-6" data-testid={`tap-item-${i}`}>
                <span className="mt-0.5 w-7 h-7 rounded-full bg-terracotta/10 text-terracotta flex items-center justify-center shrink-0">
                  <CheckCircle2 size={16}/>
                </span>
                <span className="text-stone-700 leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-stone-600 leading-relaxed">
            No more chasing committee members. No more wondering if your message was even seen.
            <span className="text-ink font-medium"> Every complaint and request reaches the right person and stays visible until it's resolved.</span>
          </p>
        </div>
      </div>
    </section>
  );
}

function ComingSoon() {
  const items = [
    {
      icon: KeyRound,
      title: "OTP-Based Gated Entry",
      body: "For gated, secure societies with access control: send a one-time entry OTP to your delivery agent or guest before they arrive. They identify themselves with the OTP at your gate, and only verified visitors are let in — no more relying on a phone call to the watchman or a guessing game at the gate.",
    },
    {
      icon: Wallet,
      title: "Online Payment Gateway (Facilitated, Not Held by Us)",
      body: "Every society will be able to connect its own payment gateway account — the one it owns and controls — so owners can pay maintenance and other dues via Net Banking, Cards, UPI, or EMI. Payments go straight from the owner into the society's own bank account; The Society OS simply facilitates the connection and reconciles the payment against the invoice. We never collect, hold, or route your society's money — that stays entirely between your owners and your bank.",
    },
  ];
  return (
    <section className="py-24 lg:py-32" data-testid="landing-coming-soon">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-12">
          <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-4 font-semibold">What's Coming Next</div>
          <h2 className="editorial text-4xl lg:text-5xl leading-tight tracking-tight">We're not stopping here.</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {items.map((it, i) => (
            <div key={i} className="relative rounded-2xl bg-white border border-stone-200 p-7" data-testid={`coming-soon-${i}`}>
              <div className="absolute top-5 right-5 inline-flex items-center gap-1.5 rounded-full bg-terracotta/10 text-terracotta text-[11px] uppercase tracking-widest font-semibold px-2.5 py-1">
                <Sparkles size={12}/> Coming Soon
              </div>
              <div className="w-11 h-11 rounded-xl bg-terracotta/10 text-terracotta flex items-center justify-center mb-5"><it.icon size={22} strokeWidth={1.5}/></div>
              <h3 className="editorial text-2xl leading-snug mb-3 pr-28">{it.title}</h3>
              <p className="text-stone-600 leading-relaxed">{it.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Governance() {
  const points = [
    { k: "Transparency", d: "Every member sees exactly where their money goes and where their request stands." },
    { k: "Accountability", d: "Every action is tied to a person, a date, and a decision — nothing is \u201Cjust verbal.\u201D" },
    { k: "Professionalism", d: "Even a fully resident-run society looks and runs like it has a dedicated management office, without needing one." },
  ];
  return (
    <section className="py-24 lg:py-32 bg-sand-100 border-y border-stone-200" data-testid="landing-governance">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-4 font-semibold">Transparency, Accountability, Professionalism</div>
          <h2 className="editorial text-4xl lg:text-5xl leading-tight tracking-tight">Brings real governance<br/>to every society.</h2>
          <p className="mt-6 text-stone-600 text-lg leading-relaxed">
            Every ownership change, every invoice, every approval, every decision — logged, timestamped, and auditable.
            No more disputes months later about who approved what. No more <em>"it was said in the meeting"</em> with
            nothing to show for it. Whether your committee changes every year or stays the same for a decade, the record
            doesn't disappear with them.
          </p>
        </div>
        <div className="lg:col-span-7 grid gap-4">
          {points.map((p, i) => (
            <div key={i} className="grid grid-cols-12 gap-4 bg-white border border-stone-200 rounded-2xl p-7 items-start" data-testid={`governance-${i}`}>
              <div className="col-span-4 lg:col-span-3">
                <div className="editorial text-2xl text-terracotta">{p.k}</div>
              </div>
              <div className="col-span-8 lg:col-span-9 text-stone-600 leading-relaxed">{p.d}</div>
            </div>
          ))}
          <div className="flex items-center gap-2 text-stone-500 text-sm pl-1"><Scale size={16}/> Records survive committee handovers.</div>
        </div>
      </div>
    </section>
  );
}

function ComparisonTable() {
  const rows = [
    ["Dues tracked in registers or spreadsheets, invoices typed out manually every cycle", "Invoices generated, verified, and posted automatically, with reminders built in"],
    ["Complaints lost in WhatsApp groups with no record of resolution", "Every complaint tracked from raised to resolved, visible to the resident"],
    ["Applications mean printed letters and personal follow-up", "Every application tracked end-to-end with a clear approval trail"],
    ["Bye-laws and notices scattered across old emails and PDFs", "One place to find every rule, notice, and past communication"],
    ["Committee handover loses institutional knowledge", "Full history retained regardless of who's on the committee"],
    ["Tenants left out of the communication loop", "Tenants included for complaints, notices, and emergency alerts"],
    ["Booking the clubhouse means a phone call and a register", "Book any common resource in a few taps, with instant confirmation"],
  ];
  return (
    <section id="comparison" className="py-24 lg:py-32" data-testid="landing-comparison">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-12">
          <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-4 font-semibold">With vs. Without — A Quick Comparison</div>
          <h2 className="editorial text-4xl lg:text-5xl leading-tight tracking-tight">What changes<br/>when you switch.</h2>
        </div>

        {/* Desktop / tablet: side-by-side table */}
        <div className="hidden md:block rounded-2xl border border-stone-200 bg-white overflow-hidden">
          <div className="grid grid-cols-2 text-xs uppercase tracking-widest font-semibold border-b border-stone-200">
            <div className="p-5 bg-sand-100 text-stone-600 flex items-center gap-2"><ArrowDownRight size={14} className="text-stone-500"/> Without The Society OS</div>
            <div className="p-5 bg-white text-terracotta flex items-center gap-2"><ArrowUpRight size={14}/> With The Society OS</div>
          </div>
          {rows.map((r, i) => (
            <div key={i} className={`grid grid-cols-2 ${i < rows.length - 1 ? "border-b border-stone-200" : ""}`} data-testid={`comparison-row-${i}`}>
              <div className="p-5 lg:p-6 text-stone-500 leading-relaxed border-r border-stone-200 bg-sand-50/60 line-through decoration-stone-300/70 decoration-1">{r[0]}</div>
              <div className="p-5 lg:p-6 text-stone-800 leading-relaxed flex items-start gap-3">
                <CheckCircle2 size={16} className="text-emerald-700 mt-1 shrink-0"/>
                <span>{r[1]}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile: stacked cards */}
        <div className="md:hidden space-y-4">
          {rows.map((r, i) => (
            <div key={i} className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
              <div className="p-4 bg-sand-100 text-xs uppercase tracking-widest font-semibold text-stone-600 flex items-center gap-1.5"><ArrowDownRight size={12}/> Without</div>
              <div className="p-4 text-stone-500 leading-relaxed line-through decoration-stone-300/70 decoration-1 border-b border-stone-200 text-sm">{r[0]}</div>
              <div className="p-4 text-xs uppercase tracking-widest font-semibold text-terracotta flex items-center gap-1.5"><ArrowUpRight size={12}/> With</div>
              <div className="p-4 text-stone-800 leading-relaxed text-sm flex items-start gap-2.5">
                <CheckCircle2 size={14} className="text-emerald-700 mt-0.5 shrink-0"/>
                <span>{r[1]}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { n: "01", t: "Share your society's data", d: "Buildings, units, owners, and current occupancy, using our upload template." },
    { n: "02", t: "Committee & residents get access", d: "OTP-based login, no passwords, ready on day one." },
    { n: "03", t: "Everything runs through the system", d: "Invoices, notices, complaints, approvals, bookings — all in one place." },
  ];
  return (
    <section id="how" className="py-24 lg:py-32 bg-sand-100 border-y border-stone-200" data-testid="landing-how">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-4 font-semibold">How It Works</div>
          <h2 className="editorial text-4xl lg:text-5xl leading-tight tracking-tight">Getting started<br/>is simple.</h2>
        </div>
        <div className="lg:col-span-8 space-y-4">
          {steps.map((s, i) => (
            <div key={i} className="grid grid-cols-12 gap-6 bg-white border border-stone-200 rounded-2xl p-8" data-testid={`how-step-${i}`}>
              <div className="col-span-2 editorial text-3xl text-terracotta">{s.n}</div>
              <div className="col-span-10">
                <div className="editorial text-2xl mb-2">{s.t}</div>
                <div className="text-stone-600 leading-relaxed">{s.d}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  const tiers = [
    { name: "Starter", price: "₹ 49", note: "per unit / year", desc: "Up to 60 units. Ideal for small residential societies.", features: ["OTP member login", "Invoices & PDFs", "Notices with letterhead", "Complaints tracker"] },
    { name: "Growth", price: "₹ 39", note: "per unit / year", desc: "60–250 units. Popular for RWAs and mid-sized complexes.", features: ["Everything in Starter", "Maker-checker governance", "Facility Bookings", "Priority support"], featured: true },
    { name: "Enterprise", price: "Custom", note: "annual", desc: "250+ units, commercial complexes, or multi-society management.", features: ["Everything in Growth", "Custom onboarding", "SLA-backed support", "Dedicated advisor"] },
  ];
  return (
    <section id="pricing" className="py-24 lg:py-32" data-testid="landing-pricing">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-4 font-semibold">Simple, per-unit pricing</div>
          <h2 className="editorial text-4xl lg:text-5xl leading-tight tracking-tight">One invoice a year.<br/>No surprises.</h2>
          <p className="mt-4 text-stone-500 text-sm italic">Illustrative pricing — final rates confirmed at onboarding.</p>
        </div>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {tiers.map((t) => (
            <div key={t.name} data-testid={`pricing-${t.name.toLowerCase()}`}
              className={`relative rounded-2xl border p-8 flex flex-col ${t.featured ? "border-terracotta bg-white shadow-[0_8px_30px_rgb(30,63,99,0.14)]" : "border-stone-200 bg-white"}`}>
              {t.featured && <div className="absolute -top-3 left-8 rounded-full bg-terracotta text-white text-xs font-medium px-3 py-1">Most chosen</div>}
              <div className="editorial text-2xl">{t.name}</div>
              <div className="mt-6 flex items-baseline gap-2"><div className="editorial text-5xl">{t.price}</div><div className="text-stone-500 text-sm">{t.note}</div></div>
              <p className="mt-4 text-stone-600 leading-relaxed">{t.desc}</p>
              <ul className="mt-6 space-y-2.5 text-sm text-stone-700 flex-1">
                {t.features.map((f, i) => <li key={i} className="flex items-start gap-2"><CheckCircle2 size={16} className="text-emerald-700 mt-0.5"/> {f}</li>)}
              </ul>
              <a href="#contact" className={`mt-8 inline-flex justify-center items-center rounded-full px-6 py-3 font-medium text-sm transition-colors ${t.featured ? "bg-terracotta hover:bg-terracotta-hover text-white" : "border border-stone-300 hover:bg-sand-100"}`}>Enquire</a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContactForm() {
  const [form, setForm] = useState({ name: "", society_name: "", unit_count: "", phone: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/public/enquiry", { ...form, unit_count: form.unit_count ? Number(form.unit_count) : null });
      toast.success("Thanks — we'll be in touch within 1 business day.");
      setForm({ name: "", society_name: "", unit_count: "", phone: "", email: "", message: "" });
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };
  return (
    <section id="contact" className="py-24 lg:py-32 bg-sand-100 border-y border-stone-200" data-testid="landing-contact">
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-4 font-semibold">Request a Demo</div>
        <h2 className="editorial text-4xl lg:text-5xl leading-tight tracking-tight">Tell us about your society.</h2>
        <p className="mt-4 text-stone-600 leading-relaxed">A short conversation is enough for us to understand your setup and share a live demo.</p>
        <form onSubmit={submit} className="mt-10 grid md:grid-cols-2 gap-4" data-testid="contact-form">
          <Field label="Your name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="contact-name"/>
          <Field label="Society name" value={form.society_name} onChange={(v) => setForm({ ...form, society_name: v })} testid="contact-society"/>
          <Field label="Unit count" type="number" value={form.unit_count} onChange={(v) => setForm({ ...form, unit_count: v })} testid="contact-units"/>
          <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="contact-phone"/>
          <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="contact-email" className="md:col-span-2"/>
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Message</label>
            <textarea className="mt-2 w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-base focus:border-terracotta focus:ring-2 focus:ring-terracotta/20 outline-none min-h-[110px]"
              value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message"/>
          </div>
          <div className="md:col-span-2 flex items-center gap-3">
            <button type="submit" disabled={busy} data-testid="contact-submit"
              className="inline-flex items-center gap-2 rounded-full bg-terracotta hover:bg-terracotta-hover disabled:opacity-50 text-white px-8 py-3.5 text-base font-medium transition-colors">
              {busy ? "Sending…" : "Send enquiry"} <ArrowRight size={18}/>
            </button>
            <span className="text-sm text-stone-500">We reply within one business day.</span>
          </div>
        </form>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, type = "text", required, testid, className = "" }) {
  return (
    <div className={className}>
      <label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">{label}{required && " *"}</label>
      <input type={type} value={value} required={required} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-2 w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-base focus:border-terracotta focus:ring-2 focus:ring-terracotta/20 outline-none"/>
    </div>
  );
}

function Footer() {
  return (
    <footer className="border-t border-stone-200 py-16 bg-sand-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Trust & Security band */}
        <div className="rounded-2xl border border-stone-200 bg-white p-7 lg:p-9 mb-14 grid lg:grid-cols-12 gap-6 items-start" data-testid="trust-security">
          <div className="lg:col-span-3">
            <div className="w-11 h-11 rounded-xl bg-terracotta/10 text-terracotta flex items-center justify-center mb-3"><ShieldCheck size={22} strokeWidth={1.5}/></div>
            <div className="editorial text-2xl leading-snug">Trust &amp; Security</div>
          </div>
          <p className="lg:col-span-9 text-stone-600 leading-relaxed">
            Your society's data is sensitive — ownership records, financials, personal contact details. The Society OS
            is built on <span className="text-ink font-medium">bank-grade encryption</span>, hosted entirely within
            India, with every action logged and auditable. Your residents' trust is the whole point of going digital —
            we don't take shortcuts with it.
          </p>
        </div>

        <div className="grid gap-10 lg:grid-cols-3">
          <div>
            <BrandMark size={56} />
            <p className="text-sm text-stone-600 mt-3 max-w-md leading-relaxed">
              Operated by <span className="font-semibold text-ink">Reliserve Platforms Private Limited</span>.
              We partner with Residential and Commercial RWAs to simplify the handling process and management of the society.
            </p>
            <div className="mt-4 flex items-center gap-3 text-xs text-stone-500">
              <ShieldCheck size={12}/> Bank-grade encryption · India-only data residency · Full audit trails
            </div>
          </div>
          <div className="text-sm text-stone-500">
            <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-3">Company</div>
            <div className="space-y-1.5 leading-relaxed">
              <div>Reliserve Platforms Private Limited</div>
              <div className="text-xs">Registered in India</div>
            </div>
          </div>
          <div className="text-sm text-stone-500 lg:text-right">
            <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-3">Product</div>
            <div className="flex flex-col gap-1.5 lg:items-end">
              <Link to="/admin/login" className="hover:text-ink">Portal login</Link>
              <a href="#pricing" className="hover:text-ink">Pricing</a>
              <a href="#contact" className="hover:text-ink">Contact</a>
            </div>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-stone-200 text-xs text-stone-500 flex flex-wrap justify-between gap-2">
          <div>© {new Date().getFullYear()} Reliserve Platforms Private Limited. All rights reserved.</div>
          <div>Made in India · Hosted in India</div>
        </div>
      </div>
    </footer>
  );
}
